import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { BondsDetailsType, BondsGeneralType } from '../../sj-orm/models/assets/categories';
import { BondsEnum, BondsKind, RatingType, TradeType } from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export const BondsList = [
  {
    label: 'forms_assets:bonds.enums.category.corporate',
    key: BondsEnum.corporate,
  },
  {
    label: 'forms_assets:bonds.enums.category.municipal',
    key: BondsEnum.municipal,
  },
  {
    label: 'forms_assets:bonds.enums.category.sovereign',
    key: BondsEnum.sovereign,
  },
  {
    label: 'forms_assets:bonds.enums.category.investmentAndStructural',
    key: BondsEnum.investmentAndStructural,
  },
];

const TradeTypesList = [
  { label: 'forms_assets:bonds.enums.tradeType.etc', key: TradeType.etc },
  { label: 'forms_assets:bonds.enums.tradeType.otc', key: TradeType.otc },
];

const RatingTypesList = [
  { label: 'forms_assets:bonds.enums.rating.AAA', key: RatingType.AAA },
  { label: 'forms_assets:bonds.enums.rating.AA', key: RatingType.AA },
  {
    label: 'forms_assets:bonds.enums.rating.A',
    key: RatingType.A,
  },
  {
    label: 'forms_assets:bonds.enums.rating.BBB',
    key: RatingType.BBB,
  },
  { label: 'forms_assets:bonds.enums.rating.bb', key: RatingType.bb },
  {
    label: 'forms_assets:bonds.enums.rating.B',
    key: RatingType.B,
  },
  { label: 'forms_assets:bonds.enums.rating.CCCp', key: RatingType.CCCp },
  { label: 'forms_assets:bonds.enums.rating.CCC', key: RatingType.CCC },
  {
    label: 'forms_assets:bonds.enums.rating.C_CCCm',
    key: RatingType.C_CCCm,
  },
  { label: 'forms_assets:bonds.enums.rating.D', key: RatingType.D },
];

const BondsKindList = [
  { label: 'forms_assets:bonds.enums.bondsKind.constantCoupon', key: BondsKind.constantCoupon },
  { label: 'forms_assets:bonds.enums.bondsKind.fixedRate', key: BondsKind.fixedRate },
  { label: 'forms_assets:bonds.enums.bondsKind.floaters', key: BondsKind.floaters },
  { label: 'forms_assets:bonds.enums.bondsKind.discount', key: BondsKind.discount },
  { label: 'forms_assets:bonds.enums.bondsKind.inflationLinked', key: BondsKind.inflationLinked },
  { label: 'forms_assets:bonds.enums.bondsKind.investment', key: BondsKind.investment },
  { label: 'forms_assets:bonds.enums.bondsKind.structured', key: BondsKind.structured },
  { label: 'forms_assets:bonds.enums.bondsKind.other', key: BondsKind.other },
];
export type Bond = BondsGeneralType &
  BondsDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

type BondsHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR;
  amount: QuestionType.NUMBER;
  holder: QuestionType.PICK_ARRAY_OF_DTO;
};

type BondsHelpDetailsType = {
  issuedBy?: QuestionType.TEXT;
  isin?: QuestionType.TEXT;
  ticker?: QuestionType.TEXT;
  bondsKind?: QuestionType.SELECT_FROM_LIST;
  acquisitionDate?: QuestionType.DATE_TIME;
  maturityDate?: QuestionType.DATE_TIME;
  brokerageAccountNumber?: QuestionType.TEXT;
  depositaryName?: QuestionType.TEXT;
  depositaryAccountNumber?: QuestionType.TEXT;
  seller?: QuestionType.TEXT;
  tradeType?: QuestionType.SELECT_FROM_LIST;
  tradingPlatform?: QuestionType.TEXT;
  secured?: QuestionType.CHECKBOX;
  bondsRate?: QuestionType.SELECT_FROM_LIST;
  strategy?: QuestionType.TEXT;
  attachments?: QuestionType.FILE_UPLOAD;
};

type BondsFormGeneralType = TDtoWrapper<BondsGeneralType, BondsHelpGeneralType>;
type BondsFormDetailsType = TDtoWrapper<BondsDetailsType, BondsHelpDetailsType>;

const BondsFormGeneral: BondsFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:bonds.inputs.type.label',
    tooltip: 'forms_assets:bonds.inputs.type.tooltip',
    value: BondsEnum.corporate,
    items: BondsList,
    required: true,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:bonds.inputs.amount.label',
    value: 0,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:bonds.inputs.marketValue.label',
    tooltip: 'forms_assets:bonds.inputs.marketValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  holder: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:bonds.inputs.holder.label',
    value: [],
    colName: CollectionName.CONTACTS,
    tooltip: 'forms_assets:bonds.inputs.holder.tooltip',
    filter: (contact) =>
      [ContactType.BROKER, ContactType.BANK].includes((contact as unknown as ContactDto).type),
    dtoType: [ContactType.BANK, ContactType.BROKER],
  },
};
const BondsFormDetails: BondsFormDetailsType = {
  issuedBy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.issuedBy.label',
    order: 1,
  },
  isin: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.isin.label',
    tooltip: 'forms_assets:bonds.inputs.isin.tooltip',
    order: 2,
  },
  ticker: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.ticker.label',
    order: 3,
  },
  bondsKind: {
    type: QuestionType.SELECT_FROM_LIST,
    items: BondsKindList,
    label: 'forms_assets:bonds.inputs.bondsKind.label',
    order: 4,
  },
  acquisitionDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:bonds.inputs.acquisitionDate.label',
    order: 5,
  },
  maturityDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:bonds.inputs.maturityDate.label',
    order: 6,
  },
  brokerageAccountNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.brokerageAccountNumber.label',
    order: 7,
  },
  depositaryName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.depositaryName.label',
    order: 8,
  },
  depositaryAccountNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.depositaryAccountNumber.label',
    order: 9,
  },
  tradeType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:bonds.inputs.tradeType.label',
    items: TradeTypesList,
    order: 10,
  },
  tradingPlatform: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.tradingPlatform.label',
    showIf: {
      tradeType: {
        value: TradeType.etc,
      },
    },
    order: 11,
  },
  secured: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:bonds.inputs.secured.label',
    order: 12,
  },
  seller: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.seller.label',
    showIf: {
      tradeType: {
        value: TradeType.otc,
      },
    },
    order: 13,
  },
  bondsRate: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:bonds.inputs.bondsRate.label',
    items: RatingTypesList,
    order: 14,
  },
  strategy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bonds.inputs.strategy.label',
    order: 15,
  },
  attachments: baseAssetFields.attachments,
  // insuranceAgreement: baseAassetFields.insuranceAgreement,
};

const BondsForm = [
  BondsFormGeneral,
  BondsFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { BondsForm };
